import classNames from 'volta/helpers/class-names';
import getStrict from 'volta/helpers/get-strict';
import orElse from 'volta/helpers/or-else';
import startWith from 'volta/helpers/start-with';
import tElse from 'volta/helpers/t-else';
import BvBadge from 'volta/components/bv-badge';
import BvBtn from 'volta/components/bv-btn';
import BvCollapsible from 'volta/components/bv-collapsible';
import BvFormLayout from 'volta/components/bv-form-layout';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvModal from 'volta/components/bv-modal';
import BvPopover from 'volta/components/bv-popover';
import BvText from 'volta/components/bv-text';
import BaseFilter from '../filters/base-filter';
import ConnectedFilterControlItem from './connected-filter-control-item';
import FilterTags from './filter-tags';
import SearchInput from './search-input';
import { concat, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import t from 'ember-intl/helpers/t';
import onResize from 'ember-on-resize-modifier/modifiers/on-resize';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import media from 'ember-responsive/helpers/media';
import { set } from 'ember-set-helper';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';

import type { IResourceName } from 'volta/models/base-model';

const FILTER_FIELD_MIN_WIDTH = 150;

export interface BvFiltersSignature {
  Args: {
    onClearAll?: () => void;
    belowAppHeader?: boolean;
    moreFiltersButtonLabel?: string;
    isLoading?: boolean;
    focused?: boolean;
    disabled?: boolean;
    onSearchFocus?: (e: Event) => void;
    onSearchBlur?: (e: Event) => void;
    resourceName: IResourceName;
    queryPlaceholder?: string;
    hideTextSearch?: boolean;
    hideTags?: boolean;
    filterDefinitions: TFilterDefinition[];
    filterValues: TFilterValues;
    forceShowMoreFiltersButton?: boolean;
    onFiltersApply?: (filterValues: TFilterValues) => void;
    onFilterValueChange?: (filterValue?: TFilterValue) => void;
    onSearchChange?: (filterValues: TFilterValues, query: string) => Promise<unknown> | unknown;
    onClose?: () => void;
    searchValue?: string;
    searchKey?: string;
  };
  Blocks: {
    default: [{ toggleFilters: () => void }];
    tagsRight?: [{ toggleFilters: () => void }];
  };
  Element: HTMLDivElement;
}

export default class BvFiltersComponent extends Component<BvFiltersSignature> {
  <template>
    {{#let (and @hideTags this.enabledFiltersCount) as |_showNoTags|}}
      {{! template-lint-disable no-at-ember-render-modifiers }}
      <div class='bv-filters' ...attributes {{didInsert this.handleDidInsert}}>
        {{!filtersControlMarkup}}
        {{! Only used to measure buttons when real buttons are hidden }}
        {{#if (and this.popoverableFilterDefinitions this.popoverableFilterDefinitions.length)}}
          {{! template-lint-disable no-at-ember-render-modifiers }}
          <div
            class='bv-filters-connected-filter-control__proxy-button-container'
            aria-hidden='true'
            {{didInsert this.handleProxyContainerInsert}}
            {{createRef 'proxyButtonContainer' bucket=this}}
          >
            {{#each this.popoverableFilterDefinitions key='queryParam' as |definition|}}
              <div data-id={{definition.queryParam}}>
                {{#let
                  (if
                    (startWith definition.queryParam 'customFields_')
                    (tElse definition.queryParam definition.title)
                    (or
                      definition.title
                      (tElse
                        (concat 'filterDefinitions.' definition.queryParam)
                        (tElse definition.queryParam)
                      )
                    )
                  )
                  as |title|
                }}
                  <BvBtn @disclosure='auto' @text={{title}} />
                {{/let}}
              </div>
            {{/each}}
          </div>
        {{/if}}
        <BvInline class='bv-filters-connected-filter-control__wrapper' @gap='4'>
          <div
            class='bv-filters-connected-filter-control bv-filters-connected-filter-control--right'
            {{createRef 'container' bucket=this}}
            {{onResize this.handleResize debounce=40}}
          >
            {{#unless @hideTextSearch}}
              <div class='bv-filters-connected-filter-control__center-container'>
                <ConnectedFilterControlItem>
                  <SearchInput
                    @placeholder={{if
                      _showNoTags
                      (t 'filterDefinitions.filterSummary' filterCount=this.enabledFiltersCount)
                      @queryPlaceholder
                    }}
                    @resourceName={{@resourceName}}
                    @filterValues={{@filterValues}}
                    @searchValue={{@searchValue}}
                    @onSearchBlur={{@onSearchBlur}}
                    @onSearchFocus={{@onSearchFocus}}
                    @onSearchChange={{@onSearchChange}}
                    @disabled={{@disabled}}
                    @focused={{@focused}}
                    @iconColor={{if _showNoTags 'green'}}
                  />
                </ConnectedFilterControlItem>
              </div>
            {{/unless}}
            {{!rightMarkup}}
            {{#if this.popoverableFilterDefinitions}}
              <div
                class='bv-filters-connected-filter-control__right-container
                  {{unless
                    this.shouldRenderMoreFiltersButton
                    " bv-filters-connected-filter-control__right-container--without-more-filters"
                  }}'
              >
                {{#each this.filtersToRender key='queryParam' as |definition|}}
                  <ConnectedFilterControlItem>
                    <BvPopover @size='md' @disabled={{@disabled}} as |popover|>
                      <popover.Trigger>
                        {{#let
                          (if
                            (startWith definition.queryParam 'customFields_')
                            (tElse definition.queryParam definition.title)
                            (or
                              definition.title
                              (tElse
                                (concat 'filterDefinitions.' definition.queryParam)
                                (t definition.queryParam)
                              )
                            )
                          )
                          as |title|
                        }}
                          <BvBtn @disclosure='auto' @text={{title}} />
                        {{/let}}
                      </popover.Trigger>
                      <popover.Content>
                        <popover.Body>
                          <BaseFilter
                            @filterValues={{@filterValues}}
                            @definition={{definition}}
                            @resourceName={{@resourceName}}
                            @initialValue={{getStrict @filterValues definition.queryParam}}
                            @onChange={{this.filterValuesDidChange}}
                            @clearDefaultValue={{definition.clearDefaultValue}}
                          />
                        </popover.Body>
                      </popover.Content>
                    </BvPopover>
                  </ConnectedFilterControlItem>
                {{/each}}
              </div>
            {{/if}}
            {{!rightActionMarkup}}
            {{#if this.shouldRenderMoreFiltersButton}}
              <div
                class='bv-filters-connected-filter-control__more-filters-button-container
                  {{unless
                    this.filtersToRender.length
                    "bv-filters-connected-filter-control__more-filters-button-container--only-button-visible"
                  }}'
                {{createRef 'moreFiltersButtonContainer' bucket=this}}
              >
                <ConnectedFilterControlItem>
                  <div {{createRef 'moreFiltersButtonContainerToggle' bucket=this}}>
                    <BvBtn
                      @onClick={{this.toggleFilters}}
                      @disabled={{@disabled}}
                      @testName='more-filters-button'
                    >
                      <BvText
                        class={{if _showNoTags 'u-color-primary'}}
                        @text={{orElse @moreFiltersButtonLabel (t 'bvFilters.moreFilters')}}
                      />
                      {{#if _showNoTags}}
                        <BvBadge
                          @text='{{this.enabledFiltersCount}}'
                          @color='primary'
                          @size='sm'
                          class='u-ml-2'
                        />
                      {{/if}}
                    </BvBtn>
                  </div>
                </ConnectedFilterControlItem>
              </div>
            {{/if}}
          </div>
          {{!auxMarkup}}
          {{#if (has-block)}}
            <div class='u-flex-grow-0'>
              {{yield (hash toggleFilters=this.toggleFilters)}}
            </div>
          {{/if}}
        </BvInline>
        {{!filtersContainerMarkup}}
        {{#if this.isMoreFiltersContainerVisible}}
          <BvModal
            @size='sm'
            @translucentBackdrop={{true}}
            @sheet={{true}}
            @belowAppHeader={{orElse @belowAppHeader true}}
            @onClose={{this.closeFilters}}
            class='bv-filters-modal'
            as |modal|
          >
            {{#if (media 'isMobile')}}
              <div class='bv-filters__filters-container-header'>
                <BvBtn
                  @iconLeft='close'
                  @subtle={{true}}
                  @accessibilityLabel={{t 'bvFilters.cancel'}}
                  @onClick={{this.closeFilters}}
                />
                <BvText @variant='heading-lg' @as='p'>
                  {{t 'bvFilters.moreFilters'}}
                </BvText>
                <BvBtn
                  @onClick={{this.closeFilters}}
                  @color='primary'
                  @text={{t 'bvFilters.done'}}
                />
              </div>
              <modal.Body @scrollable={{true}} class='bv-filters__filters-mobile-container-content'>
                {{!filtersContentMarkup}}
                <BvFormLayout as |layout|>
                  {{#each @filterDefinitions key='queryParam' as |definition|}}
                    {{#unless definition.hidden}}
                      <layout.Item>
                        <BaseFilter
                          @filterValues={{@filterValues}}
                          @definition={{definition}}
                          @resourceName={{@resourceName}}
                          @initialValue={{getStrict @filterValues definition.queryParam}}
                          @onChange={{this.filterValuesDidChange}}
                          @clearDefaultValue={{definition.clearDefaultValue}}
                        />
                      </layout.Item>
                    {{/unless}}
                  {{/each}}
                </BvFormLayout>
                {{!filtersMobileFooterMarkup}}
                <div class='bv-filters__filters-mobile-container-footer'>
                  <div class='bv-filters__empty-footer-state'>
                    <BvText @color='subdued' @as='p'>
                      {{t 'bvFilters.noFiltersApplied'}}
                    </BvText>
                  </div>
                </div>
              </modal.Body>
            {{else}}
              {{!filtersDesktopHeaderMarkup}}
              <modal.Header
                class='bv-filters__filters-container-header'
                @title={{t 'bvFilters.moreFilters'}}
              />
              <modal.Body
                @sectioned={{false}}
                @scrollable={{true}}
                class='bv-filters__filters-desktop-container-content'
              >
                <modal.Section>
                  {{!filtersContentMarkup}}
                  <BvFormLayout as |layout|>
                    {{#each @filterDefinitions key='queryParam' as |definition|}}
                      {{#if (not (or definition.hidden definition.customFieldDefinition))}}
                        <layout.Item>
                          <BaseFilter
                            @filterValues={{@filterValues}}
                            @definition={{definition}}
                            @resourceName={{@resourceName}}
                            @initialValue={{getStrict @filterValues definition.queryParam}}
                            @onChange={{this.filterValuesDidChange}}
                            @clearDefaultValue={{definition.clearDefaultValue}}
                          />
                        </layout.Item>
                      {{/if}}
                    {{/each}}
                  </BvFormLayout>
                </modal.Section>
                {{#if this.customFilterDefinitions.length}}
                  <button
                    class='bv-filters__filter-trigger'
                    type='button'
                    aria-controls='customFieldsCollapsible'
                    aria-expanded={{this.customFiltersOpen}}
                    {{on 'click' (set this 'customFiltersOpen' (not this.customFiltersOpen))}}
                  >
                    <div class='bv-filters__filter-trigger-label-container'>
                      <div class='bv-filters__filter-trigger-title'>
                        <BvText @text={{t 'attributes'}} />
                        <BvBadge class='u-ml-2' @text='{{this.customFilterDefinitions.length}}' />
                      </div>
                      <BvIcon
                        @glyph='chevron-forward-outline'
                        class={{classNames
                          'u-animate-transform'
                          (if this.customFiltersOpen 'u-rotate-90')
                        }}
                      />
                    </div>
                  </button>
                  <BvCollapsible @open={{this.customFiltersOpen}} @id='customFieldsCollapsible'>
                    <modal.Section>
                      <BvFormLayout as |layout|>
                        {{#each this.customFilterDefinitions key='queryParam' as |def|}}
                          <layout.Item>
                            <BaseFilter
                              @filterValues={{@filterValues}}
                              @definition={{def}}
                              @resourceName={{@resourceName}}
                              @initialValue={{getStrict @filterValues def.queryParam}}
                              @onChange={{this.filterValuesDidChange}}
                              @clearDefaultValue={{def.clearDefaultValue}}
                            />
                          </layout.Item>
                        {{/each}}
                      </BvFormLayout>
                    </modal.Section>
                  </BvCollapsible>
                {{/if}}
              </modal.Body>
              {{!filtersDesktopFooterMarkup}}
              <modal.Footer class='bv-filters__filters-container-footer'>
                <BvBtn
                  @onClick={{optional @onClearAll}}
                  @disabled={{not this.enabledFiltersCount}}
                  @text={{t 'bvFilters.clearAllFilters'}}
                />
                <BvBtn
                  @onClick={{this.closeFilters}}
                  @color='primary'
                  @text={{t 'bvFilters.done'}}
                />
              </modal.Footer>
            {{/if}}
          </BvModal>
        {{/if}}
        {{!tagsMarkup}}
        <BvInline @gap='4' @align='space-between' @blockAlign='center'>
          <div class='u-flex-1'>
            {{#if (and (not @hideTags) this.enabledFiltersCount)}}
              <FilterTags
                class='bv-filters__tags-container'
                @resourceName={{@resourceName}}
                @disabled={{@disabled}}
                @filterValues={{this.enabledFilters}}
                @filterDefinitions={{@filterDefinitions}}
                @onFilterValuesChange={{this.filterValuesDidChange}}
              />
            {{/if}}
          </div>
          {{#if (has-block 'tagsRight')}}
            {{yield (hash toggleFilters=this.toggleFilters) to='tagsRight'}}
          {{/if}}
        </BvInline>

        {{!backdropMarkup}}
      </div>
    {{/let}}
  </template>
  @tracked availableWidth = 0;
  @tracked proxyButtonsWidth: Record<string, number> = {};
  @tracked isMoreFiltersContainerVisible = false;
  @tracked customFiltersOpen = false;

  searchOnInput = true;

  @ref('container') container?: HTMLElement;
  @ref('proxyButtonContainer') proxyButtonContainer?: HTMLElement;
  @ref('moreFiltersButtonContainerToggle') moreFiltersButtonContainerToggle?: HTMLElement;
  @ref('moreFiltersButtonContainer') moreFiltersButtonContainer?: HTMLElement;

  // Getters
  // ~~~~~

  @cached
  get definitionsDict() {
    const dict: Record<string, TFilterDefinition> = {};
    this.args.filterDefinitions.forEach((def) => (dict[def.queryParam ?? ''] = def));
    return dict;
  }

  get searchKey() {
    return this.args.searchKey ?? 'q';
  }

  get popoverableFilterDefinitions() {
    const { filterDefinitions } = this.args;
    return filterDefinitions ? filterDefinitions.filter((def) => def.isPrimary && !def.hidden) : [];
  }

  get filtersToRender() {
    const { availableWidth, proxyButtonsWidth, popoverableFilterDefinitions } = this;
    const filtersToReturn = [];
    let remainingWidth = availableWidth;
    for (let i = 0; remainingWidth > 0 && i < popoverableFilterDefinitions.length; i++) {
      const filter = popoverableFilterDefinitions[i]!;
      const actionWidth = proxyButtonsWidth[filter?.queryParam ?? ''];
      if (actionWidth && actionWidth <= remainingWidth) {
        filtersToReturn.push(filter);
        remainingWidth -= actionWidth;
      } else {
        // When we can't fit an action, we break the loop.
        // The ones that didn't fit will be accessible through the "More filters" button
        break;
      }
    }
    return filtersToReturn;
  }

  get enabledFilters() {
    const { filterValues } = this.args;
    const filters: Record<string, TFilterValue> = {};

    for (const field in filterValues) {
      if (Object.prototype.hasOwnProperty.call(filterValues, field) && field !== this.searchKey) {
        const filter = filterValues[field];
        if (filter && filter.enabled) {
          filters[field] = filter;
        }
      }
    }

    return filters;
  }

  get enabledFiltersCount() {
    return Object.keys(this.enabledFilters).length;
  }

  get shouldRenderMoreFiltersButton() {
    const { forceShowMoreFiltersButton, filterDefinitions } = this.args;
    const { filtersToRender } = this;
    const filtersWithoutSearch = filterDefinitions
      ? filterDefinitions.filter((f) => !f.hidden).length
      : 0;
    return (
      forceShowMoreFiltersButton ??
      (filtersToRender && filtersToRender.length !== filtersWithoutSearch)
    );
  }

  get customFilterDefinitions() {
    return (this.args.filterDefinitions ?? []).filter((f) => f.customFieldDefinition !== undefined);
  }

  // Actions
  // ~~~~~~

  @action
  handleResize() {
    this.measureProxyButtons(this.proxyButtonContainer);
    this.measureAvailableWidth();
  }

  @action
  handleProxyContainerInsert(container: HTMLElement) {
    this.measureProxyButtons(container);
    this.measureAvailableWidth();
  }

  @action
  handleDidInsert(_container: HTMLElement) {
    this.handleResize();
  }

  @action
  toggleFilters() {
    this.isMoreFiltersContainerVisible = !this.isMoreFiltersContainerVisible;
    if (!this.isMoreFiltersContainerVisible) {
      this.args.onClose?.();
    }
  }

  @action
  closeFilters() {
    this.isMoreFiltersContainerVisible = false;
    this.args.onClose?.();
  }

  @action
  filterValuesDidChange(value: TFilterValue, field: string) {
    const { filterValues, onFiltersApply, onFilterValueChange } = this.args;
    const updatedFilterValues = { ...filterValues };

    const filterValueForField: TFilterValue = {
      ...(updatedFilterValues?.[field] ?? ({} as TFilterValue)),
      ...value
    };
    updatedFilterValues[field] =
      filterValueForField.values.length > 0 ? filterValueForField : undefined;

    onFilterValueChange?.(filterValueForField);

    if (Object.keys(updatedFilterValues).length === 0) {
      // no filter added
      return false;
    }

    return onFiltersApply?.(updatedFilterValues);
  }

  // Helpers
  // ~~~~~~

  measureProxyButtons(proxyButtonContainer?: HTMLElement) {
    if (proxyButtonContainer) {
      const proxyButtonsWidth: Record<string, number> = {};
      // this number is magical, but tweaking it solved the problem of items overlapping
      const tolerance = 52;
      [...proxyButtonContainer.children].forEach((element) => {
        const buttonWidth = element.getBoundingClientRect().width + tolerance;
        // @ts-expect-error
        const buttonKey = element.dataset.id as string;
        if (buttonKey) {
          proxyButtonsWidth[buttonKey] = buttonWidth;
        }
      });

      this.proxyButtonsWidth = proxyButtonsWidth;
    }
  }

  measureAvailableWidth() {
    if (this.container && this.moreFiltersButtonContainer) {
      const containerWidth = this.container.getBoundingClientRect().width;
      const moreFiltersButtonWidth = this.moreFiltersButtonContainer.getBoundingClientRect().width;
      const filtersActionWidth = 0;
      this.availableWidth =
        containerWidth - FILTER_FIELD_MIN_WIDTH - moreFiltersButtonWidth - filtersActionWidth;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvFilters: typeof BvFiltersComponent;
    'bv-filters': typeof BvFiltersComponent;
  }
}
